import {
  decodeFromBase64,
  doesExceedFileSizeLimit,
  getContentType,
  getContentTypeCategory,
  getPathFromUrl,
  isCustomCodeSnippetRule,
  isImageVariable,
  isReplaceImageRule,
  isReplaceImageWithVariableRule,
  isSameVariable,
  isSupportedTransaction,
  isTextVariable,
  isUrl,
  normalizeTransaction,
  transactionsCountLimit,
} from '@carboncopyco/editor-utils';
import {
  type BlockInteractionsRule,
  type CustomModifier,
  type FindReplaceRule,
  GroupSchemaType,
  GuidanceTooltipSchemaType,
  type IAIPromptGroup,
  IActionType,
  type IBlockInteractionsSettings,
  type ICustomTemplateChart,
  type IDynamicEditData,
  IDynamicEditType,
  IEditActionsType,
  type IEditorDraft,
  IEditorStatus,
  IFindAndReplaceRuleType,
  type IGuidanceTooltips,
  type IImageReplaceRule,
  type IImageReplaceWithVariableRule,
  type ILinkReplaceRule,
  type IOrganizationData,
  type ISnippetRule,
  type ITextReplaceRule,
  type IUser,
  type IVariable,
  type ImageFileMetadata,
  type ImageVariable,
  type MockResponseModifier,
  type Modification,
  NavigationEditSchemaType,
  type RecordingType,
  type Selector,
  type SnippetType,
  type TableCraftFunction,
  type TableCraftObject,
  type TableCraftRow,
  type TransactionRule,
  type EditorPerformanceResult,
} from '@carboncopyco/types';
import { base64toFile, performanceResultsDefault } from '@carboncopyco/utils';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import Emittery from 'emittery';
import { v4 as uuid } from 'uuid';
import type { CommunicationLayer } from '../services/communication';
import { eventEmitterLogger } from '../services/eventEmitterLogger';
import type {
  APIEndpoints,
  Action,
  DatalessMessageNames,
  EditorEngineMessages,
  EditorMode,
  EditorSandBoxEvents,
  EditorSdkEvents,
  FindAndReplaceMode,
  Metadata,
  StartPayload,
  TableCraftObjectsStatus,
  Transaction,
} from '../types';
import { type AssistTypes, assistType } from '../types';
import { sumPerformanceResults } from './performance';
import { cloneDeep } from 'lodash-es';

interface ImageDetails {
  url: string;
  metadata: ImageFileMetadata;
}

type NextEditorLoadedCallbacks = (() => void)[];

export type FindReplaceRuleWithoutSnippet = Exclude<
  FindReplaceRule,
  ISnippetRule
>;

const createEventEmitter = () =>
  new Emittery<EditorSdkEvents>({
    debug: {
      name: 'EditorSDK',
      enabled: true,
      logger: eventEmitterLogger,
    },
  });

const LAST_MODIFICATION_TIME_OFFSET = 60 * 1000; // 60sec

const sdkLogger = scopedLogger(Scope.EDITOR_SDK);

export class EditorSDK {
  private sdkId: string;
  private presentationDomain: string;
  private mode: EditorMode;
  private flags: Record<string, any> = {};
  private eventEmitter = createEventEmitter();
  private communicationLayer: CommunicationLayer;
  public organizationData: IOrganizationData;
  public editorOrigin: string;
  public userData: IUser;
  public api?: APIEndpoints;
  public draft: IEditorDraft;
  public filesServiceDomain: string;
  private iframeId: string;
  private unsubscribe;
  // allEdits present all the edits (Draft and non-Draft)
  private allEdits: IDynamicEditData[] = [];
  private nextEditorLoadedCallbacks: NextEditorLoadedCallbacks = [];

  // dynamic state
  public spotEditsModifications: Modification<'edit'>[] = [];
  public loaded = false;
  public modifications: Modification[] = [];
  public tableCraftObjects: {
    data: TableCraftObject[];
    status: TableCraftObjectsStatus;
  } = {
    data: [],
    status: 'loading',
  };
  public tableCraftRows: {
    data: TableCraftRow[];
    status: 'loading' | 'success';
  } = {
    data: [],
    status: 'loading',
  };
  public tableCraftFunctions: {
    data: TableCraftFunction[];
    status: 'loading' | 'success';
  } = {
    data: [],
    status: 'loading',
  };
  // edits and redos present all the Draft edits
  public edits: IDynamicEditData[] = [];
  public redos: IDynamicEditData[] = [];
  public chartsTemplates: ICustomTemplateChart[] = [];
  public variables: IVariable[] = [];
  public findAndReplaceRules: FindReplaceRuleWithoutSnippet[] = [];
  public codeSnippets: ISnippetRule[] = [];
  public inspectAction: IEditActionsType = null;
  public recordingType: RecordingType = 'cloner';
  public metadata: Metadata = {};
  public transactions: Transaction[] = [];
  public transactionModifications: Modification<'transaction_rule'>[] = [];
  public navigationModifications: Modification<NavigationEditSchemaType.blockInteractions>[] =
    [];
  public isGuidanceTooltipsEditLoading = false;
  public isGuidanceTooltipOpen = false;
  public guidanceTooltipsModifications: Modification<GuidanceTooltipSchemaType.guidanceTooltip>[] =
    [];
  public blockInteractionsSettings: Modification<NavigationEditSchemaType.blockInteractionsSettings> =
    {} as Modification<NavigationEditSchemaType.blockInteractionsSettings>;

  public aiPromptGroup: Modification<GroupSchemaType.aiPromptGroup>[] =
    {} as Modification<GroupSchemaType.aiPromptGroup>[];

  public performanceResults = cloneDeep(performanceResultsDefault);
  public isPerformanceMeasuringStarted = false;
  public performanceRunIterationsChecked = false;
  public performanceSelectorXpath = '';

  constructor({
    id,
    communicationLayer,
  }: {
    id: string;
    communicationLayer: CommunicationLayer;
  }) {
    this.sdkId = id ?? uuid();
    this.communicationLayer = communicationLayer;
    console.debug('[EditorSDK] constructor.');
  }

  public init() {
    this.unsubscribe = this.communicationLayer.subscribe(this.onMessageHandler);
  }

  public destroy() {
    this.unsubscribe?.();
  }

  // Callbacks
  public subscribe = <T extends keyof EditorSdkEvents>(
    eventName: T,
    callback: (data: EditorSdkEvents[T]) => void
  ) => {
    return this.eventEmitter.on(eventName, callback);
  };

  // Setters
  public setFilesServiceDomain = (domain: string) => {
    this.filesServiceDomain = domain;
  };

  public setFlags = (flags: Record<string, any>) => {
    // Launchdarkly returns a Proxy object, so we need to spread it to a new object
    this.flags = { ...flags };
  };

  public setRecordingType = async (recordingType: RecordingType) => {
    this.recordingType = recordingType;
    sdkLogger.info(`Recording type updated (recordingType: ${recordingType})`, {
      recordingType,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit('updatedRecordingType', recordingType);
  };

  public setIframeId = (iframeId: string) => {
    this.iframeId = iframeId;
  };

  public setMode = (mode: EditorMode) => {
    this.mode = mode;
  };

  public setUserData = (userData: IUser) => {
    this.userData = userData;
  };
  public setOrganizationData = (organizationData: IOrganizationData) => {
    this.organizationData = organizationData;
  };

  public set externalAPI(api: APIEndpoints) {
    this.api = api;
  }

  private setLoaded = async (isLoaded: boolean) => {
    this.loaded = isLoaded;
    await this.eventEmitter.emit('loaded', isLoaded);
  };

  public setDraft = async (draft: IEditorDraft) => {
    console.debug('[EditorSDK] set draft');
    this.draft = draft;
    await this.eventEmitter.emit('updatedDraft', draft);
  };

  private setInspectAction = async (type: IEditActionsType) => {
    this.inspectAction = type;
    this.sendMessage(IActionType.setEditMode, type);
    sdkLogger.info(`Inspect action updated (${type})`, {
      editActionType: type,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit('updatedInspectAction', type);
  };

  public startFindAndReplaceMode = (mode: FindAndReplaceMode) => {
    this.setInspectAction(IEditActionsType.inspect);
    this.sendMessage(IActionType.startFindAndReplaceMode, mode);
    this.eventEmitter.emit('startFindAndReplaceMode', mode);
  };

  public stopFindAndReplaceMode = () => {
    this.sendMessage(IActionType.stopFindAndReplaceMode);
    this.eventEmitter.emit('stopFindAndReplaceMode');
  };

  public startBlockInteractionsMode = () => {
    this.setInspectAction(IEditActionsType.inspect);
    this.sendMessage(IActionType.startBlockInteractionsMode);
    this.eventEmitter.emit('startBlockInteractionsMode');
  };

  public stopBlockInteractionsMode = () => {
    this.sendMessage(IActionType.stopBlockInteractionsMode);
    this.eventEmitter.emit('stopBlockInteractionsMode');
  };

  public startGuidanceTooltipsMode = () => {
    this.setInspectAction(IEditActionsType.inspect);
    this.sendMessage(IActionType.startGuidanceTooltipsMode);
    this.eventEmitter.emit('startGuidanceTooltipsMode');
  };

  public stopGuidanceTooltipsMode = () => {
    this.sendMessage(IActionType.stopGuidanceTooltipsMode);
    this.eventEmitter.emit('stopGuidanceTooltipsMode');
  };

  public highlightGuidanceTooltip = (
    modification: Modification<GuidanceTooltipSchemaType.guidanceTooltip>
  ) => {
    this.sendMessage(IActionType.highlightGuidanceTooltip, modification);
  };

  public destroyHighlightGuidanceTooltip = (
    modification: Modification<GuidanceTooltipSchemaType.guidanceTooltip>
  ) => {
    this.sendMessage(IActionType.destroyHighlightGuidanceTooltip, modification);
  };

  public navigateToModification = (modification: Modification) => {
    this.sendMessage(IActionType.navigateToModification, modification);
  };

  public updateInspectAction = (type: IEditActionsType) => {
    if (this.inspectAction === type) {
      return;
    }
    this.setInspectAction(type);
  };

  public highlightTarget = (edit: IDynamicEditData) => {
    this.sendMessage(IActionType.highlightTarget, edit);
  };

  public destroyHighlightTarget = (edit: IDynamicEditData) => {
    this.sendMessage(IActionType.destroyHighlightTarget, edit);
  };

  public navigateToTarget = (edit: IDynamicEditData) => {
    this.sendMessage(IActionType.navigateToTarget, edit);
  };

  public updateUrl = (url: string) => {
    this.sendMessage(IActionType.updateUrl, { url });
  };

  public reloadEditor = async () => {
    await this.recorderSessionRefresh();
    this.sendMessage(IActionType.reloadEditorFrame);
    await this.showBackdropOverlay(true);
  };

  private onDeleteModificationsError = (error: Error) => {
    console.error('[EditorSDK] delete modifications failed:', error);
    this.sendMessage(IActionType.notify, {
      type: 'error',
      message: 'Failed to delete edits, refresh the page and try again.',
    });
  };

  public deleteSpotEditsModifications = async (edits: IDynamicEditData[]) => {
    const modificationsToDelete = this.spotEditsModifications.filter(
      (modification) =>
        edits.some((e) => e.timestamp === modification.content.timestamp)
    );

    const modificationsToDeletePromise = modificationsToDelete.map(
      (modification) =>
        this.api?.deleteModification({
          modificationId: modification.id,
        })
    );

    const editsToRestoreGuidanceModification = edits.filter(
      (edit) => edit.type === IDynamicEditType.delete
    );

    const editsToRestoreGuidanceModificationPromise =
      editsToRestoreGuidanceModification.map((edit) =>
        this.sendMessage(IActionType.restoreGuidanceModification, edit)
      );

    return Promise.all([
      modificationsToDeletePromise,
      editsToRestoreGuidanceModificationPromise,
    ])
      .then(this.reloadEditor)
      .catch(this.onDeleteModificationsError);
  };

  public changeModificationToBeGlobal = async (
    edit: IDynamicEditData,
    isGlobal: boolean
  ) => {
    const modificationToUpdate = this.spotEditsModifications.find(
      (modification) => edit.timestamp === modification.content.timestamp
    );

    return this.api
      ?.updateModification({
        modificationId: modificationToUpdate.id,
        content: {
          ...modificationToUpdate.content,
          isGlobal: isGlobal,
        },
      })
      .then(() => {
        this.sendMessage(IActionType.updateRuleGlobalStatus, {
          id: `${modificationToUpdate.content.timestamp}`,
          isGlobal: isGlobal,
        });
      })
      .catch((error) => {
        console.error('[EditorSDK] update modifications global failed:', error);
        this.sendMessage(IActionType.notify, {
          type: 'error',
          message:
            'Failed to update the modification, refresh the page and try again.',
        });
      });
  };

  public setTableCraftObjects = async (queryParams?: {
    searchQuery?: string;
    page?: number;
    size?: number;
  }) => {
    const response = await this.api?.getTableCraftObjects?.(queryParams);

    const isError =
      'message' in response && response.message === 'clone status is Error';

    const isIndexing = 'status' in response && response.status === 'indexing';

    if (isIndexing || isError) {
      this.tableCraftObjects = {
        data: [],
        status: isError ? 'error' : 'indexing',
      };
      await this.eventEmitter.emit(
        'updatedTableCraftObjects',
        this.tableCraftObjects
      );

      return this.tableCraftObjects;
    }

    const tableCraftObjects = isIndexing
      ? []
      : (response as TableCraftObject[]);

    return this.setTableCraftObjectsResult(tableCraftObjects, 'success');
  };

  private setTableCraftObjectsResult = async (
    data: TableCraftObject[],
    status: TableCraftObjectsStatus = 'success'
  ) => {
    this.tableCraftObjects = { data, status };
    sdkLogger.info('TableCraft objects updated', {
      tableCraftObjects: data.map(({ display_name, filter_columns, hide }) => ({
        display_name,
        filter_columns,
        hide,
      })),
      tableCraftObjectsCount: data.length,
    });
    await this.eventEmitter.emit(
      'updatedTableCraftObjects',
      this.tableCraftObjects
    );

    return this.tableCraftObjects;
  };

  public getTableCraftRows = async (
    objectId: string,
    queryParams?: {
      searchQuery?: string;
      page?: number;
      size?: number;
    }
  ) => {
    const rows = await this.api?.getTableCraftRows?.(objectId, queryParams);
    this.tableCraftRows = {
      data: rows ?? [],
      status: 'success',
    };
    await this.eventEmitter.emit('updatedTableCraftRows', this.tableCraftRows);
    return this.tableCraftRows;
  };

  public getTableCraftFunctions = async () => {
    const response = await this.api?.getTableCraftFunctions?.();
    this.tableCraftFunctions = {
      data: response.functions ?? [],
      status: 'success',
    };
    await this.eventEmitter.emit(
      'updatedTableCraftFunctions',
      this.tableCraftFunctions
    );
    return this.tableCraftFunctions;
  };

  public getTableCraftEnrichmentColumns = async ({
    objectId,
    col_name,
  }: {
    objectId: string;
    col_name;
  }) =>
    this.api?.getTableCraftEnrichmentColumns?.({
      objectId,
      col_name,
    });

  public setTableCraftRow = async (payload: {
    objectId: string;
    column: string;
    value: string | boolean;
    row_id: string;
    id_column: string;
    enrich: boolean;
  }) => {
    return this.api?.updateTableCraftRow?.(payload);
  };

  public setTableCraftColumn = async (data: {
    objectId: string;
    col_name: string;
    generator: string;
    generator_args?: Record<string, unknown> | string;
    enrich;
  }) => {
    return this.api?.updateTableCraftColumn?.(data);
  };

  public setTableCraftSettings = async (data: {
    objectId: string;
    hide: boolean;
    display_name: string;
    filter_columns: string[];
    set_object_pk: string;
  }) => {
    return this.api?.updateTableCraftSettings?.(data);
  };

  public revertTableCraftModification = async (data: {
    objectId: string;
    entity: 'cell' | 'column' | 'row';
    row_id: string;
    col_name: string;
    enrich: boolean;
  }) => {
    return this.api?.revertTableCraftModification?.(data);
  };

  public filterModification = (modifications: Modification[]) =>
    this.flags.editorHideObjectEditingDataRules
      ? modifications.filter(({ content }) => {
          const isObjectEditing = !!(
            content as {
              rules: {
                modifiers: { type: 'ObjectEditing' }[];
              }[];
            }
          ).rules?.some((rule) =>
            rule.modifiers.some(({ type }) => type === 'ObjectEditing')
          );
          return !isObjectEditing;
        })
      : modifications;

  public setModifications = async (modifications: Modification[]) => {
    this.modifications = this.filterModification(modifications);
    sdkLogger.info('Modifications updated', {
      modifications: this.modifications.map(({ content }) => content),
      modificationsCount: this.modifications.length,
      ...this.getAssetInfo(),
    });
    this.sendMessage(IActionType.setModifications, this.modifications);

    const sortedByModificationType = this.modifications.reduce(
      (result, modification) => {
        const { modificationType, isDraft, content, schemaType } = modification;
        if (!isDraft) {
          return result;
        }
        if (schemaType === IFindAndReplaceRuleType.replaceLink) {
          return {
            ...result,
            rule: [...result.rule, content] as FindReplaceRule[],
          };
        }
        // in case of a transaction or navigation rule we want to save the whole modification
        if (modificationType === 'transaction_rule') {
          return {
            ...result,
            transactionRule: [...result.transactionRule, modification],
          };
        }

        if (modificationType === 'navigation_rule') {
          if (
            schemaType === NavigationEditSchemaType.blockInteractionsSettings
          ) {
            return {
              ...result,
              blockInteractionsSettings: modification,
            };
          }
          return {
            ...result,
            navigationRule: [...result.navigationRule, modification],
          };
        }

        if (modificationType === 'tooltip') {
          return {
            ...result,
            guidanceRule: [...result.guidanceRule, modification],
          };
        }

        if ((modificationType as 'settings') === 'settings') return result;

        if (modificationType === 'group') {
          return {
            ...result,
            aiPromptGroup: [...result.aiPromptGroup, modification],
          };
        }

        if (modificationType === 'rule') {
          const newContent = {
            ...(content as FindReplaceRule),
            ...(modification.groupId ? { groupId: modification.groupId } : {}),
          };

          return {
            ...result,
            rule: [...result.rule, newContent] as FindReplaceRule[],
          };
        }

        return {
          ...result,
          [modificationType]: [...result[modificationType], content],
        };
      },
      {
        edit: [] as IDynamicEditData[],
        rule: [] as FindReplaceRule[],
        variable: [] as IVariable[],
        transactionRule: [] as Modification<'transaction_rule'>[],
        navigationRule:
          [] as Modification<NavigationEditSchemaType.blockInteractions>[],
        guidanceRule:
          [] as Modification<GuidanceTooltipSchemaType.guidanceTooltip>[],
        blockInteractionsSettings:
          {} as Modification<NavigationEditSchemaType.blockInteractionsSettings>,
        aiPromptGroup: [] as Modification<GroupSchemaType.aiPromptGroup>[],
      }
    );

    await this.setEdits(sortedByModificationType.edit.reverse());
    await this.setRules(sortedByModificationType.rule);
    await this.setVariables(sortedByModificationType.variable);
    await this.setTransactionModifications(
      sortedByModificationType.transactionRule
    );
    await this.setNavigationModifications(
      sortedByModificationType.navigationRule
    );
    await this.setGuidanceTooltipsModifications(
      sortedByModificationType.guidanceRule
    );
    await this.setBlockInteractionsSettings(
      sortedByModificationType.blockInteractionsSettings
    );
    await this.setSpotEditsModifications(
      modifications.filter((modification) => modification.schemaType === 'edit')
    );
    await this.setAiPromptModifications(sortedByModificationType.aiPromptGroup);

    this.allEdits = this.modifications
      .filter(
        (modification): modification is Modification<'edit'> =>
          modification.schemaType === 'edit'
      )
      .map(({ content }) => content);
    sdkLogger.info(`All edits (schemaType === "edit") updated`, {
      allEdits: this.allEdits,
      allEditsCount: this.allEdits.length,
      ...this.getAssetInfo(),
    });

    await this.eventEmitter.emit('updatedModifications', this.modifications);
  };

  private setEdits = async (edits: IDynamicEditData[]) => {
    console.debug('[EditorSDK] set edits');
    sdkLogger.info('Edits updated', {
      edits,
      editsCount: edits.length,
      ...this.getAssetInfo(),
    });
    this.edits = edits;
    await this.eventEmitter.emit('updatedEdits', this.edits);
    await this.eventEmitter.emit('updatedRedos', this.redos);
  };

  private setSpotEditsModifications = async (
    spotEditsModifications: Modification<'edit'>[]
  ) => {
    this.spotEditsModifications = spotEditsModifications;
    sdkLogger.info('Spot edits modifications updated', {
      spotEditsModifications,
      spotEditsModificationsLength: spotEditsModifications.length,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit(
      'updatedSpotEditsModifications',
      this.spotEditsModifications
    );
  };

  public undoEdit = () => {
    this.redos = [this.edits[0], ...this.redos];
    const payload = this.edits[0];

    return this.api
      ?.deleteModification({
        modificationId: this.getEditModificationId(payload.timestamp),
      })
      .then(() => this.sendMessage(IActionType.undoElement, payload));
  };

  public redoEdit = () => {
    const payload = this.redos[0];
    this.redos.shift();

    return this.api
      ?.createModification({
        schemaType: 'edit',
        content: payload,
      })
      .then(() => this.sendMessage(IActionType.redoElement, payload));
  };

  private getEditModificationId = (timestamp: number) => {
    const modification = this.modifications
      .filter(
        (modification): modification is Modification<'edit'> =>
          modification.schemaType === 'edit'
      )
      .find((modification) => modification.content.timestamp === timestamp);
    return modification?.id;
  };

  private setChartTemplates = async (
    chartTemplates: ICustomTemplateChart[]
  ) => {
    this.chartsTemplates = chartTemplates;
    this.sendMessage(IActionType.setChartsTemplates, this.chartsTemplates);
    await this.eventEmitter.emit('updatedChartTemplates', this.chartsTemplates);
  };

  public fetchChartTemplates = () => {
    return this.api?.getChartTemplates?.().then(this.setChartTemplates);
  };

  public createChartTemplate = (chartTemplate: ICustomTemplateChart) => {
    return this.api
      ?.createChartTemplate?.({
        ...chartTemplate,
        version: '1.0.0',
      })
      .then((data) => {
        this.setChartTemplates([...this.chartsTemplates, data]);
        return data;
      });
  };

  public updateChartTemplate = ({
    chartId,
    payload,
  }: {
    chartId: string;
    payload: ICustomTemplateChart;
  }) => {
    return this.api?.updateChartTemplate?.({ chartId, payload }).then(() => {
      this.setChartTemplates(
        this.chartsTemplates.map((chart) =>
          chart.id === chartId ? { ...chart, ...payload } : chart
        )
      );
    });
  };

  public deleteChartTemplate = (id: string) => {
    return this.api?.deleteChartTemplate?.(id).then(() => {
      this.setChartTemplates(this.chartsTemplates.filter((t) => t.id !== id));
    });
  };

  private setVariables = async (variables: IVariable[]) => {
    console.debug('[EditorSDK] set variables');
    sdkLogger.info('Variables updated', {
      variables,
      variablesCount: variables.length,
      ...this.getAssetInfo(),
    });
    this.variables = variables;
    this.sendMessage(IActionType.setVariables, variables);
    await this.eventEmitter.emit('updatedVariables', variables);
  };

  public reorderVariables = async (moved: IVariable, after?: IVariable) => {
    const movedId = this.getVariableModificationId(moved);
    const afterId = after ? this.getVariableModificationId(after) : null;

    return await this.api?.reorderModification?.({
      id: movedId,
      afterId: afterId,
    });
  };

  public createVariable = async (variable: IVariable) => {
    if (isImageVariable(variable)) {
      const [fileServiceLink] = await this.uploadImages([variable.value]);
      variable.value = fileServiceLink;
      return this.api?.createModification({
        schemaType: 'image_variable',
        content: variable,
      });
    }
    if (isTextVariable(variable)) {
      return this.api?.createModification({
        schemaType: 'variable',
        content: variable,
      });
    }
  };

  public editVariable = async (variable: IVariable) => {
    const originalVariable = this.variables.find((variableObj) =>
      isSameVariable(variableObj, variable)
    );

    if (!originalVariable) {
      return;
    }

    const modificationId = this.getVariableModificationId(variable);

    if (
      isImageVariable(variable) &&
      !variable.value.includes(originalVariable.value)
    ) {
      const [fileServiceLink] = await this.uploadImages([variable.value]);
      variable.value = fileServiceLink;
    }
    return this.api?.updateModification({
      modificationId,
      content: variable,
    });
  };

  private getVariableModificationId(variable: IVariable) {
    const modification = this.modifications
      .filter(
        (modification): modification is Modification<'variable'> =>
          modification.modificationType === 'variable'
      )
      .find(
        (modification) =>
          modification.content.variableId === variable.variableId
      );
    return modification?.id;
  }

  public deleteVariable = async (variable: IVariable) => {
    // deleting an image variable needs to delete also all the associated F&R rules
    if (isImageVariable(variable)) {
      const rules = this.findAndReplaceRules
        .filter(isReplaceImageWithVariableRule)
        .filter(
          ({ destinationVariableId }) =>
            destinationVariableId === variable.variableId
        );

      await this.deleteFindAndReplaceRules(rules);
    }

    return this.api?.deleteModification({
      modificationId: this.getVariableModificationId(variable),
    });
  };

  private setRules = async (rules: FindReplaceRule[]) => {
    await this.setFindAndReplaceRules(
      rules.filter(
        ({ type }) => type !== IFindAndReplaceRuleType.snippet
      ) as FindReplaceRuleWithoutSnippet[]
    );
    await this.setCodeSnippets(rules.filter(isCustomCodeSnippetRule));
    this.sendMessage(IActionType.setRules, rules);
  };

  private setFindAndReplaceRules = async (
    rules: FindReplaceRuleWithoutSnippet[]
  ) => {
    console.debug('[EditorSDK] set find and replace rules');
    sdkLogger.info('Find and replace rules updated', {
      findAndReplaceRules: rules,
      findAndReplaceRulesCount: rules.length,
      ...this.getAssetInfo(),
    });

    this.findAndReplaceRules = rules;

    await this.eventEmitter.emit(
      'updatedFindAndReplaceRules',
      this.findAndReplaceRules
    );
    await this.eventEmitter.emit('updatedAiPromptGroup', this.aiPromptGroup);
  };

  private setCodeSnippets = async (snippets: ISnippetRule[]) => {
    console.debug('[EditorSDK] set code snippets');
    sdkLogger.info('Code snippets updated', {
      snippetsCount: snippets.length,
      snippets,
      ...this.getAssetInfo(),
    });

    this.codeSnippets = snippets;

    await this.eventEmitter.emit('updatedCodeSnippets', this.codeSnippets);
  };

  public createFindAndReplaceRule = async (
    rule: Omit<FindReplaceRuleWithoutSnippet, 'ruleId'>,
    groupId?: string
  ) => {
    const ruleId = uuid();
    const newRule = { ...rule, ruleId } as Exclude<
      FindReplaceRule,
      ISnippetRule
    >;

    return this.api
      ?.createModification?.({
        schemaType: rule.type,
        content: newRule,
        groupId: groupId,
      })
      .then((data) => this.onUpdatedFindAndReplaceRules([data]));
  };

  public reorderFindAndReplaceRules = async (
    moved: FindReplaceRuleWithoutSnippet,
    after?: FindReplaceRuleWithoutSnippet
  ) => {
    const movedId = this.getRuleModificationId(moved);
    const afterId = after ? this.getRuleModificationId(after, true) : null;

    return await this.api?.reorderModification?.({
      id: movedId,
      afterId: afterId,
    });
  };

  public editFindAndReplaceRule = async (
    rule: FindReplaceRuleWithoutSnippet,
    selectors?: Selector[]
  ) => {
    // added to rule group id to work with groups
    // it's not part of the content so remove it to prevent api error
    delete rule['groupId'];
    return this.api
      ?.updateModification?.({
        modificationId: this.getRuleModificationId(rule),
        content: rule,
        selectors: selectors,
      })
      .then((data) => this.onUpdatedFindAndReplaceRules([data]));
  };

  private getRuleModificationId(
    rule: FindReplaceRule,
    getLastGroupIndex = false
  ) {
    if (
      (rule as unknown as Modification<GroupSchemaType.aiPromptGroup>)
        .modificationType === 'group'
    ) {
      return getLastGroupIndex
        ? this.getLastRuleOfGroupModificationId(
            rule as unknown as Modification<GroupSchemaType.aiPromptGroup>
          )
        : // No rule types have id
          (rule as unknown as { id: string }).id;
    }
    const modification = this.modifications
      .filter(
        (
          modification
        ): modification is Modification<
          | 'replace_text'
          | 'replace_image'
          | 'replace_image_with_variable'
          | 'snippet'
          | 'replace_link'
        > =>
          modification.modificationType === 'rule' ||
          modification.modificationType === 'navigation_rule'
      )
      .find((modification) => modification.content.ruleId === rule.ruleId);
    return modification?.id;
  }

  private getLastRuleOfGroupModificationId(
    modification: Modification<GroupSchemaType.aiPromptGroup>
  ) {
    const groupId = modification.content.id;
    let lastGroupIndex = -1;
    this.modifications.forEach((mod, index) => {
      if (mod?.groupId === groupId) {
        lastGroupIndex = index;
      }
    });
    return this.modifications[lastGroupIndex]?.id;
  }

  public deleteFindAndReplaceRule = async (
    rule: FindReplaceRuleWithoutSnippet
  ) => {
    return this.api
      ?.deleteModification({
        modificationId: this.getRuleModificationId(rule),
      })
      .then((data) => this.onUpdatedFindAndReplaceRules([data]));
  };

  private deleteFindAndReplaceRules = async (
    rules: FindReplaceRuleWithoutSnippet[]
  ) => {
    return Promise.all(
      rules.map((rule) => {
        return this.api?.deleteModification({
          modificationId: this.getRuleModificationId(rule),
        });
      })
    ).then((data) => this.onUpdatedFindAndReplaceRules(data));
  };

  private onUpdatedFindAndReplaceRules = async (rules: Modification<any>[]) => {
    if (
      rules
        .map((rule) => rule.content)
        .some(
          (rule: FindReplaceRule) =>
            isReplaceImageRule(rule) || isReplaceImageWithVariableRule(rule)
        )
    ) {
      await this.recorderSessionRefresh();
      this.sendMessage(IActionType.setRules, this.findAndReplaceRules);
    }
    return rules;
  };

  private listenForKeyboardNavigation = () => {
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      const { code, ctrlKey, metaKey, altKey } = e;
      const isMacOS = navigator.userAgent.search('Mac') !== -1;
      const keyboardShortcutPressed = (isMacOS ? metaKey : ctrlKey) && altKey;

      if (keyboardShortcutPressed) {
        const action = {
          KeyN: IEditActionsType.navigate,
          KeyE: IEditActionsType.inspect,
        }[code];

        if (action) {
          this.updateInspectAction(action);
        }
      }
    });
  };

  private startEditor = () => {
    if (!this.mode) {
      return console.debug('[EditorSDK] start editor failed, mode is not set.');
    }

    if (this.mode === 'editor') {
      this.startEditorMode();
    } else if (this.mode === 'preview') {
      this.startPreviewMode();
    }
  };

  private saveEditHandler = (editData: IDynamicEditData) => {
    const createEdit = () => {
      this.api
        ?.createModification?.({
          schemaType: 'edit',
          content: editData,
        })
        .then((res) => {
          console.debug('[EditorSDK] edit has been saved successfully:', res);
        })
        .catch((err) => {
          console.error('[EditorSDK] create edit failed:', err);
        });
    };

    if (editData.type === IDynamicEditType.image) {
      if (
        getPathFromUrl(editData.element.new.value) ===
        getPathFromUrl(editData.element.old.value)
      ) {
        createEdit();
        this.sendMessage(IActionType.redoElement, editData);
      } else {
        this.uploadImage(editData.element.new.value)
          .then((res) => {
            editData.element.new.value = res?.link_to_file;
            createEdit();
            this.sendMessage(IActionType.redoElement, editData);
          })
          .catch((err) => {
            console.error('[EditorSDK] upload image failed', err);
            this.sendMessage(IActionType.undoElement, editData);
          });
      }
    } else {
      createEdit();
    }
  };

  private deleteEdits = (editIds: number[]) => {
    return Promise.all(
      editIds.map(this.getEditModificationId).map((modificationId) =>
        this.api?.deleteModification({
          modificationId,
        })
      )
    ).catch((err) => {
      console.error('[EditorSDK] delete edits failed:', err);
      this.sendMessage(IActionType.notify, {
        type: 'error',
        message: 'Failed to reset edits, refresh the page and try again.',
      });
    });
  };

  private sendUserData = () => {
    this.sendMessage(IActionType.setUserData, {
      organizationId: String(this.organizationData?.id),
      organization: this.organizationData,
      ...this.userData,
    });
  };

  private runNextEditorLoadedCallbacks = () => {
    this.nextEditorLoadedCallbacks.forEach((callback) => {
      callback();
    });
    this.nextEditorLoadedCallbacks = [];
  };

  public setNextEditorLoadedCallbacks = (
    actions: NextEditorLoadedCallbacks
  ) => {
    actions.forEach((callback) => {
      this.nextEditorLoadedCallbacks.push(callback);
    });
  };

  private getAssetInfo = () => {
    return {
      assetInfo: {
        recordingType: this.recordingType,
        mode: this.mode ?? 'unknown',
        sourceType: this.draft?.sourceType ?? 'unknown',
        draftId: this.draft?.id ?? 'unknown',
        sourceId: this.draft?.sourceId ?? 'unknown',
      },
    };
  };

  private onEditorLoadedHandler = (origin: string) => {
    console.debug('[EditorSDK] editor loaded.');
    this.editorOrigin = origin; // get the origin of the editor inside an iframe.
    console.debug('[EditorSDK] editor origin:', this.editorOrigin);
    this.presentationDomain = `${window.location.origin}${window.location.pathname}`;

    this.startEditor();
    this.sendUserData();
    this.setLoaded(true);

    sdkLogger.info('Editor loaded', {
      editorOrigin: this.editorOrigin,
      presentationDomain: this.presentationDomain,
      organizationId: this.organizationData?.id,
      userId: this.userData?.id,
      ...this.getAssetInfo(),
    });
  };

  private fetchChartsTemplates = () => {
    this.api
      ?.getChartTemplates?.()
      .then((templates) => {
        console.debug('[EditorSDK] sending charts templates:', templates);
        this.chartsTemplates = templates;
      })
      .catch(() => {
        console.debug('[EditorSDK] Failed to load chart templates.');
      })
      .finally(() => {
        this.sendChartsTemplates();
      });
  };

  private sendChartsTemplates = () => {
    this.sendMessage(IActionType.setChartsTemplates, this.chartsTemplates);
  };

  private lockTimeoutPassed(timestamp) {
    // Convert the Unix timestamp to milliseconds
    const timestampInMs = timestamp * 1000;

    const timestampWithAddition = timestampInMs + LAST_MODIFICATION_TIME_OFFSET;

    const currentTime = Date.now();

    return currentTime > timestampWithAddition;
  }

  private checkIsDemoModified = () => {
    if (!this.modifications.length) {
      return;
    }

    const { creator, createTime } =
      this.modifications[this.modifications.length - 1];

    // When demo is modified by another user, we want to display notification
    if (
      creator !== this.userData?.email &&
      this.draft?.modified &&
      this.lockTimeoutPassed(createTime)
    ) {
      this.sendMessage(IActionType.showDemoModifiedNotification, {
        userEmail: creator,
      });
      return;
    }
  };

  private onMessageHandler = async (event: Partial<MessageEvent<Action>>) => {
    const { status, type, payload, origin } = event.data ?? {};

    if (status === IEditorStatus.editorLoaded) {
      this.onEditorLoadedHandler(event.origin);
    }

    if (type === IActionType.editorSandboxEvents) {
      const domEvent = payload as EditorSandBoxEvents;
      if (domEvent === 'load') {
        this.showBackdropOverlay(false);
        await this.eventEmitter.emit('onEditorLoaded');
        // Check if there are some actions in a queue and execute them
        this.runNextEditorLoadedCallbacks();

        if (this.flags.editorMultiUserLocking) {
          this.checkIsDemoModified();
        }
      }
    }

    // Handle new edit save.
    if (type === IActionType.doneEditing) {
      console.debug('[EditorSDK] done editing', payload);
      sdkLogger.info('Done editing', { payload, ...this.getAssetInfo() });
      this.saveEditHandler(payload as IDynamicEditData);
    }

    if (type === IActionType.getChartsTemplates) {
      this.sendChartsTemplates();
    }

    if (type === IActionType.sendingMetadata) {
      this.metadata = {
        ...this.metadata,
        ...payload,
      } as Metadata;
      await this.eventEmitter.emit('updateMetadata', this.metadata);
    }

    if (type === IActionType.setEditMode && origin === 'editorEngine') {
      const actionType = payload as IEditActionsType;

      this.inspectAction = actionType;
      await this.eventEmitter.emit('updatedInspectAction', actionType);
    }

    if (type === IActionType.createRuleRequest) {
      this.createRule(payload);
    }

    if (type === IActionType.generateAiRequest) {
      this.generateAiRequest(payload);
    }

    if (type === IActionType.editCreated) {
      this.updateNavigationRule(payload);
    }

    if (type === IActionType.openUrlLink) {
      this.openUrlLink(payload);
    }

    if (type === IActionType.deleteEdits) {
      this.deleteEdits(payload);
    }

    if (type === IActionType.refreshSessionAndReload) {
      await this.recorderSessionRefresh();
      this.sendMessage(IActionType.reloadEditorFrame);
      this.showBackdropOverlay(true);
    }

    if (type === IActionType.editGuidanceModification) {
      this.editGuidanceTooltipModification(payload);
    }

    if (type === IActionType.stopIsGuidanceTooltipsEditLoading) {
      this.setIsGuidanceTooltipsEditLoading(false);
    }

    if (type === IActionType.setIsGuidanceTooltipOpen) {
      this.setIsGuidanceTooltipOpen(payload);
    }

    if (
      type === IActionType.addTransaction &&
      this.flags.editorDataRulesAdmin &&
      isSupportedTransaction(payload.original)
    ) {
      const transaction = normalizeTransaction(payload);
      this.transactions.push({
        ...transaction,
        id: uuid(),
      });
      if (this.transactions.length > transactionsCountLimit) {
        // if we exceed the limit, remove the oldest transaction
        this.transactions.shift();
      }
    }

    if (type === IActionType.createDataRuleFromElement) {
      this.createDataRuleFromElement(payload);
    }

    if (type === IActionType.deleteGuidanceModification) {
      const modification = this.modifications.find(
        (modification) => modification.id === payload.modificationId
      ) as Modification<GuidanceTooltipSchemaType>;

      if (!modification) return;

      this.editGuidanceTooltipModification({
        ...modification,
        content: {
          ...modification.content,
          isParentElementDeleted: true,
        },
      });
    }

    if (type === IActionType.restoreGuidanceModification) {
      const modification = this.modifications.find(
        (modification) => modification.id === payload.modificationId
      ) as Modification<GuidanceTooltipSchemaType>;
      if (!modification) return;

      this.editGuidanceTooltipModification({
        ...modification,
        content: {
          ...modification.content,
          isParentElementDeleted: false,
        },
      });
    }

    if (type === IActionType.visualSearch) {
      const objects = await this.visualSearch(payload);
      this.openVisualSearch({
        results: objects.data,
        htmlElementString: payload.htmlElementString,
        referer: payload.referer,
      });
    }

    if (type === IActionType.log) {
      const logPayload = payload as {
        type: 'info' | 'error' | 'warn' | 'debug';
        message: string;
        context?: Record<string, any>;
        error?: Error;
      };

      const coreLogger = scopedLogger(Scope.EDITOR_CORE);
      coreLogger[logPayload.type](
        logPayload.message,
        logPayload.context,
        logPayload.error
      );
    }

    if (type === IActionType.setPerformanceResults) {
      this.performanceResults = sumPerformanceResults(
        payload,
        this.performanceResults
      );
      this.eventEmitter.emit('performanceResults', payload);
    }

    if (type === IActionType.stopPerformanceMeasuring) {
      this.isPerformanceMeasuringStarted = false;
      await this.eventEmitter.emit('isPerformanceMeasuringStarted', false);
    }

    if (type === IActionType.stopPerformancePickSelectorMode) {
      this.handlePerformanceStopOnSelectorXpathChange(payload);
    }
  };

  public generateAiRequest = async (data: {
    task_type: AssistTypes;
    prompt: string;
    assist_data?: Record<string, any>;
  }) => {
    let genratedRulesRes = [];

    const res = await this.api.aiAssist(data);
    switch (data.task_type) {
      case assistType.FNR:
        if (Array.isArray(res.data) && res.data.length > 0) {
          genratedRulesRes = await Promise.allSettled(
            res.data.map(async (item) => {
              const rule = {
                newValue: item.replace_with,
                oldValue: item.original,
              };
              await this.createRule({
                ruleType: IFindAndReplaceRuleType.replaceText,
                ...rule,
              });
            })
          );
        }
        break;

      case assistType.FAKE_DATA_REPLACE_ASSIST:
        if (!Array.isArray(res.data) && Object.keys(res.data).length > 0) {
          genratedRulesRes = await Promise.allSettled(
            Object.entries(res.data).map(async ([key, value]) => {
              const rule = {
                newValue: value,
                oldValue: key,
              };
              await this.createRule({
                ruleType: IFindAndReplaceRuleType.replaceText,
                ...rule,
              });
            })
          );
        }
        break;

      case assistType.FAKE_DATA:
        console.warn('[EditorSDK] Fake data generation is not supported yet');
        break;

      case assistType.FAKE_DATA_REPLACE:
        console.warn('[EditorSDK] Fake data replace is not supported yet');
        break;

      default:
        console.warn('[EditorSDK] Unknown assist type');
        break;
    }

    if (genratedRulesRes.some((res) => res.status === 'rejected')) {
      console.error('[EditorSDK] Failed to generate AI rule');
      this.sendMessage(IActionType.createRuleResponse, {
        status: 'error',
      });
    }
  };

  private createDataRuleFromElement = async ({
    htmlElementString,
    referer,
  }: {
    htmlElementString: string;
    referer?: string;
  }) => {
    const { results } = await this.api.searchTransactions({
      htmlElementString,
      referer,
    });
    const transactions = results.map((transaction) => ({
      ...transaction,
      id: uuid(),
    }));
    this.eventEmitter.emit('openDataRuleCreation', transactions);
  };

  public visualSearch = async ({
    htmlElementString,
    referer,
  }: {
    htmlElementString: string;
    referer?: string;
  }) => {
    const results = await this.api.visualTableCraftSearch({
      htmlElementString,
      referer,
    });

    return {
      data: results,
      status: 'success',
    };
  };

  private openVisualSearch = ({
    results,
    htmlElementString,
    referer,
  }: {
    results: TableCraftObject[];
    htmlElementString: string;
    referer?: string;
  }) => {
    this.eventEmitter.emit('openVisualSearch', {
      results,
      htmlElementString,
      referer,
    });
  };

  public aiAssist = (props: {
    task_type: AssistTypes;
    prompt: string;
    assist_data?: Record<string, any>;
  }) => this.api.aiAssist(props);

  private updateNavigationRule = async ({ ruleType, ...payload }) => {
    const ruleCreationHandler = {
      [NavigationEditSchemaType.blockInteractions]:
        this.editNavigationModification,
      [IFindAndReplaceRuleType.replaceLink]:
        this.updateFindAndReplaceLinkModification,
    };

    try {
      await ruleCreationHandler[ruleType].call(this, payload);
      this.sendMessage(IActionType.createRuleResponse, {
        status: 'success',
      });
    } catch (error) {
      console.error('[EditorSDK] Failed to update navigation rule', error);
      this.sendMessage(IActionType.createRuleResponse, {
        status: 'error',
      });
    }
  };

  private createRule = async ({ ruleType, ...payload }) => {
    const ruleCreationHandler = {
      [IFindAndReplaceRuleType.replaceImageWithVariable]:
        this.handleCreateImageReplaceWithVariableRule,
      [IFindAndReplaceRuleType.replaceImage]: this.handleCreateImageReplaceRule,
      [IFindAndReplaceRuleType.replaceText]: this.handleCreateTextReplaceRule,
      [NavigationEditSchemaType.blockInteractions]:
        this.createNavigationModification,
      [IFindAndReplaceRuleType.replaceLink]:
        this.createNavigationModificationLinkEdit,
      [GuidanceTooltipSchemaType.guidanceTooltip]:
        this.createGuidanceTooltipModification,
    };

    try {
      await ruleCreationHandler[ruleType].call(this, payload);
      this.sendMessage(IActionType.createRuleResponse, {
        status: 'success',
      });
    } catch (error) {
      console.error('[EditorSDK] Failed to create rule', error);
      this.sendMessage(IActionType.createRuleResponse, {
        status: 'error',
      });
    }
  };

  public handleCreateTextReplaceRule = async ({
    oldValue,
    newValue,
    options,
    groupId,
  }: ITextReplaceRule & { groupId: string }) => {
    return this.createFindAndReplaceRule(
      {
        type: IFindAndReplaceRuleType.replaceText,
        oldValue,
        newValue,
        options,
        isGlobal: true,
      } as ITextReplaceRule,
      groupId
    );
  };

  private handleCreateImageReplaceRule = async ({
    sourceImage,
    destinationImage, // Backward compitability, should be removed when editor-engine sends only destination
    destination,
  }: {
    sourceImage: ImageDetails & { base64: string };
    destinationImage: ImageDetails;
    destination: ImageDetails;
  }) => {
    destinationImage = destinationImage ?? destination;
    const sourceImageBase64 = sourceImage.base64;

    const [sourceFileServiceLink, destinationFileServiceLink] =
      await this.uploadImages([sourceImageBase64, destinationImage.url]);

    return this.createFindAndReplaceRule({
      type: IFindAndReplaceRuleType.replaceImage,
      sourceUrl: sourceImage.url,
      sourceFileServiceLink,
      destinationFileServiceLink,
      sourceFileMetadata: sourceImage.metadata,
      destinationFileMetadata: destinationImage.metadata,
      isGlobal: true,
    } as IImageReplaceRule);
  };

  public uploadCodeSnippet = (snippet: string, snippetType: SnippetType) => {
    const file = new File([snippet], 'snippet.txt', {
      type: snippetType === 'css' ? 'text/css' : 'text/javascript',
    });
    return this.api?.uploadCodeSnippet?.(file);
  };

  private handleCreateImageReplaceWithVariableRule = async ({
    sourceImage,
    destination: destinationVariable,
  }: {
    sourceImage: ImageDetails & { base64: string };
    destination: ImageVariable;
  }) => {
    const sourceImageBase64 = sourceImage.base64;
    const [sourceFileServiceLink] = await this.uploadImages([
      sourceImageBase64,
    ]);

    return this.createFindAndReplaceRule({
      type: IFindAndReplaceRuleType.replaceImageWithVariable,
      sourceUrl: sourceImage.url,
      sourceFileServiceLink,
      destinationVariableId: destinationVariable.variableId,
      sourceFileMetadata: sourceImage.metadata,
      isGlobal: true,
    } as IImageReplaceWithVariableRule);
  };

  public uploadImage = (base64: string) => {
    const fileData = base64toFile(base64);
    return this.api?.uploadImage?.(fileData);
  };

  public uploadImages = (base64Images: string[]) => {
    return Promise.all(
      base64Images.map((base64) => this.uploadImage(base64))
    ).then((results) => results.map((res) => res.link_to_file));
  };

  public recorderSessionRefresh = () => {
    return this.api?.recorderSessionRefresh?.({
      recorderUuid: String(this.draft.recorderUuid),
      sessionId: this.metadata.recorderSessionId,
    });
  };

  public openUrlLink = (url: string) => {
    return this.api?.openUrlLink?.(url);
  };

  public getContentWindow = (): Window => {
    const iframe = document.getElementById(this.iframeId) as HTMLIFrameElement;

    if (iframe?.contentWindow) {
      return iframe.contentWindow;
    }
    console.debug("[EditorSDK] editor's content window is not found.");
    return null;
  };

  public sendMessage<Type extends DatalessMessageNames<EditorEngineMessages>>(
    type: Type,
    payload?: EditorEngineMessages[Type]
  ): void;
  public sendMessage<Type extends keyof EditorEngineMessages>(
    type: Type,
    payload: EditorEngineMessages[Type]
  ): void;
  public sendMessage<Type extends keyof EditorEngineMessages>(
    type: Type,
    payload: EditorEngineMessages[Type]
  ) {
    const contentWindow = this.getContentWindow();

    if (contentWindow) {
      contentWindow.postMessage(
        { type, payload, senderId: this.sdkId },
        this.editorOrigin
      );
    } else {
      this.communicationLayer.sendMessage({
        type,
        payload,
        senderId: this.sdkId,
      });
    }
  }

  private getPayload = (): StartPayload => {
    return {
      edits: this.allEdits ?? [],
      variables: this.variables ?? [],
      chartsTemplates: this.chartsTemplates,
      rules: [...this.findAndReplaceRules, ...this.codeSnippets] ?? [],
      modifications: this.modifications ?? [],
      flags: this.flags,
      origin: window.location.origin,
      mode: this.mode,
      recordingType: this.recordingType,
      filesServiceDomain: this.filesServiceDomain,
      presentationDomain: this.presentationDomain,
    };
  };

  // Modes
  public startEditorMode = () => {
    this.listenForKeyboardNavigation();
    this.updateInspectAction(IEditActionsType.navigate);
    this.fetchChartsTemplates();

    this.sendMessage(IActionType.startup, this.getPayload());
    console.debug('[EditorSDK] started editor mode');
  };

  private startPreviewMode = (): void => {
    this.sendMessage(IActionType.startPreview, this.getPayload());
    console.debug('[EditorSDK] started preview mode');
  };

  public getMetadata = () => {
    this.sendMessage(IActionType.getMetadata);
  };

  public showBackdropOverlay = async (show: boolean) => {
    return this.eventEmitter.emit('showBackdropOverlay', show);
  };

  public createCodeSnippet = async (snippet: ISnippetRule) => {
    const { source, snippetType } = snippet;

    try {
      const { link_to_file = '' } = await this.uploadCodeSnippet(
        source,
        snippetType
      );

      const newSnippet = {
        ...snippet,
        ruleId: uuid(),
        type: IFindAndReplaceRuleType.snippet,
        source: link_to_file,
        isGlobal: true,
      } satisfies ISnippetRule;

      return this.api
        ?.createModification({
          schemaType: 'snippet',
          content: newSnippet,
        })
        .then(this.onCodeSnippetUpdated);
    } catch (error) {
      console.error('[EditorSDK] Failed to create code snippet', error);
    }
  };

  public editCodeSnippet = async (snippet: ISnippetRule) => {
    const { source, snippetType } = snippet;
    const shouldUploadSnippetSrouce = !isUrl(source);

    try {
      if (shouldUploadSnippetSrouce) {
        const { link_to_file = '' } = await this.uploadCodeSnippet(
          source,
          snippetType
        );
        // convert the code to url
        snippet.source = link_to_file;
      }

      return this.api
        ?.updateModification({
          modificationId: this.getRuleModificationId(snippet),
          content: snippet,
        })
        .then(this.onCodeSnippetUpdated);
    } catch (error) {
      console.error('[EditorSDK] Failed to edit code snippet', error);
    }
  };

  public reorderCodeSnippets = async (
    moved: ISnippetRule,
    after?: ISnippetRule
  ) => {
    const movedId = this.getRuleModificationId(moved);
    const afterId = after ? this.getRuleModificationId(after) : null;

    return await this.api
      ?.reorderModification?.({
        id: movedId,
        afterId: afterId,
      })
      .then(this.onCodeSnippetUpdated);
  };

  public deleteCodeSnippet = async (snippet: ISnippetRule) => {
    return this.api
      ?.deleteModification({
        modificationId: this.getRuleModificationId(snippet),
      })
      .then(this.onCodeSnippetUpdated);
  };

  private onCodeSnippetUpdated = (snippetModification) => {
    this.sendMessage(IActionType.reloadEditorFrame);
    this.showBackdropOverlay(true);
    return snippetModification;
  };

  private setTransactionModifications = async (
    transactionModifications: Modification<'transaction_rule'>[]
  ) => {
    this.transactionModifications = transactionModifications;
    sdkLogger.info('Transaction modifications updated', {
      transactionModifications,
      transactionModificationsCount: transactionModifications.length,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit(
      'updatedTransactionModifications',
      this.transactionModifications
    );
  };

  private setNavigationModifications = async (
    navigationModifications: Modification<NavigationEditSchemaType.blockInteractions>[]
  ) => {
    this.navigationModifications = navigationModifications;
    sdkLogger.info('Navigation modifications updated', {
      navigationModifications,
      navigationModificationsCount: navigationModifications.length,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit(
      'updatedNavigationModifications',
      this.navigationModifications
    );
  };

  private setBlockInteractionsSettings = async (
    blockInteractionsSettings: Modification<NavigationEditSchemaType.blockInteractionsSettings>
  ) => {
    this.blockInteractionsSettings = blockInteractionsSettings;
    sdkLogger.info('Block interactions settings updated', {
      blockInteractionsSettings,
      blockInteractionsSettingsCount: blockInteractionsSettings.length,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit(
      'updatedBlockInteractionsSettings',
      this.blockInteractionsSettings
    );
  };

  private setGuidanceTooltipsModifications = async (
    guidanceTooltipsModifications: Modification<GuidanceTooltipSchemaType.guidanceTooltip>[]
  ) => {
    this.guidanceTooltipsModifications = guidanceTooltipsModifications;
    sdkLogger.info('Guidance tooltips modifications updated', {
      guidanceTooltipsModifications,
      guidanceTooltipsModificationsCount: guidanceTooltipsModifications.length,
      ...this.getAssetInfo(),
    });
    await this.eventEmitter.emit(
      'updatedGuidanceTooltipsModifications',
      this.guidanceTooltipsModifications
    );
  };

  public doesTransactionRuleExceedsLimitSize = (
    transactionRule: TransactionRule
  ) => {
    // Transaction rule is limited to 10kb
    const maxFileSize = 10_000;
    return doesExceedFileSizeLimit(
      JSON.stringify(transactionRule),
      maxFileSize
    );
  };

  private createResponseContentFile = (
    responseContent: string,
    contentType: string
  ) => {
    const contentTypeCategory = getContentTypeCategory(contentType);
    const fileName = 'responseContent';

    if (contentTypeCategory === 'image') {
      return base64toFile(
        `data:${contentType};base64,${responseContent}`,
        fileName,
        contentType
      );
    }

    const blob = new Blob([decodeFromBase64(responseContent)], {
      type: contentType,
    });
    return new File([blob], fileName, {
      type: contentType,
    });
  };

  private convertMockResponseModifierToUrl = async (
    modifier: MockResponseModifier
  ): Promise<MockResponseModifier> => {
    const contentType = getContentType(modifier.arguments.headers ?? []);
    const file = this.createResponseContentFile(
      modifier.arguments.content ?? '',
      contentType
    );
    const { link_to_file } =
      await this.api?.uploadTransactionResponseContent?.(file);

    return {
      ...modifier,
      arguments: {
        ...modifier.arguments,
        content: link_to_file,
        contentEncoding: 'url',
      },
    };
  };

  private convertMockResponseModifierRepresentationFields = async (
    modifier: MockResponseModifier
  ): Promise<MockResponseModifier> => {
    const blob = new Blob(
      [JSON.stringify(modifier.arguments.representationFields)],
      {
        type: 'application/json',
      }
    );
    const file = new File([blob], 'representationFields', {
      type: 'application/json',
    });

    const { link_to_file } =
      await this.api?.uploadTransactionRepresentationFields?.(file);

    const updatedModifier = structuredClone(modifier);
    delete updatedModifier.arguments.representationFields;
    updatedModifier.arguments.representationFieldsUrl = link_to_file;

    return updatedModifier;
  };

  private handleMockResponseModifier = async (
    modifier: MockResponseModifier
  ) => {
    // Transaction rule is limited to 10kb so we might need to upload the content and the representation fields to file service

    let updatedModifier = structuredClone(modifier);
    const { contentEncoding, representationType, representationFields } =
      updatedModifier.arguments;

    if (representationType === 'form' && representationFields) {
      updatedModifier =
        await this.convertMockResponseModifierRepresentationFields(
          updatedModifier
        );
    }

    if (contentEncoding === 'base64') {
      updatedModifier =
        await this.convertMockResponseModifierToUrl(updatedModifier);
    }

    return updatedModifier;
  };

  private handleTransactionRule = async (transactionRule: TransactionRule) => {
    const updatedTransactionRule = structuredClone(transactionRule);

    const mockResponseModifier = updatedTransactionRule.rules[0].modifiers.find(
      (modifier): modifier is MockResponseModifier =>
        modifier.type === 'MockResponse'
    );

    if (mockResponseModifier) {
      const updatedModifier =
        await this.handleMockResponseModifier(mockResponseModifier);
      updatedTransactionRule.rules[0].modifiers = [updatedModifier];
    }

    return updatedTransactionRule;
  };

  public createTransactionRule = async (
    transactionRule: TransactionRule & {
      isActive?: boolean;
      isLocked?: boolean;
    },
    refresh = true
  ) => {
    const updatedTransactionRule =
      await this.handleTransactionRule(transactionRule);

    const isLocked = () => {
      return updatedTransactionRule.rules[0].modifiers.some(
        (modifier: CustomModifier) => modifier.arguments.isCustomModifier
      );
    };

    return this.api
      ?.createModification({
        schemaType: 'transaction_rule',
        content: updatedTransactionRule,
        isLocked: transactionRule.isLocked ?? isLocked(),
        isActive: transactionRule.isActive,
      })
      .then((rule) => {
        this.onUpdatedTransactionRule(rule, refresh);
      });
  };

  public editTransactionModification = async (
    transactionModification: Modification<'transaction_rule'>,
    refresh = true
  ) => {
    const { id, content, isActive, isLocked } = transactionModification;
    const updatedTransactionRule = await this.handleTransactionRule(content);

    return this.api
      ?.updateModification({
        modificationId: id,
        content: updatedTransactionRule,
        isActive,
        isLocked,
      })
      .then((rule) => {
        this.onUpdatedTransactionRule(rule, refresh);
      });
  };

  public deleteTransactionModification = async (
    transactionModification: Modification<'transaction_rule'>
  ) => {
    return this.api
      ?.deleteModification({
        modificationId: transactionModification.id,
      })
      .then(this.onUpdatedTransactionRule);
  };

  public reorderTransactionModification = async (
    moved: Modification<'transaction_rule'>,
    after?: Modification<'transaction_rule'>
  ) => {
    return await this.api
      ?.reorderModification?.({
        id: moved.id,
        afterId: after?.id,
      })
      .then(this.onUpdatedTransactionRule);
  };

  private onUpdatedTransactionRule = async (
    rule: Modification<'transaction_rule'>,
    refresh = true
  ) => {
    await this.recorderSessionRefresh();
    if (refresh) {
      this.sendMessage(IActionType.reloadEditorFrame);
      this.showBackdropOverlay(true);
      this.transactions = [];
    }
    return rule;
  };

  public createNavigationModification = async (
    rule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => {
    const { content, selectors } = rule;
    const modification = {
      selectors,
      schemaType: NavigationEditSchemaType.blockInteractions,
      content: content as BlockInteractionsRule | ILinkReplaceRule,
    } as Modification<NavigationEditSchemaType.blockInteractions>;

    return this.api?.createModification(modification);
  };

  public createGuidanceTooltipModification = async (
    rule: Modification<GuidanceTooltipSchemaType.guidanceTooltip>
  ) => {
    const { content, selectors } = rule;
    const modification = {
      selectors,
      schemaType: GuidanceTooltipSchemaType.guidanceTooltip,
      content: content as IGuidanceTooltips,
    } as Modification<GuidanceTooltipSchemaType.guidanceTooltip>;

    return this.api?.createModification(modification).then((res) => {
      this.updateInspectAction(IEditActionsType.navigate);
      this.sendMessage(IActionType.openGuidanceTooltip, res);
    });
  };

  public editGuidanceTooltipModification = async (
    modification: Modification<GuidanceTooltipSchemaType.guidanceTooltip>,
    isGlobal = false
  ) => {
    return this.api
      ?.updateModification({
        modificationId: modification.id,
        content: {
          ...modification.content,
          isGlobal,
        },
      })
      .finally(() => {
        this.setIsGuidanceTooltipsEditLoading(false);
      });
  };

  public deleteGuidanceTooltipModification = async (
    guidanceTooltipRule: Modification<GuidanceTooltipSchemaType.guidanceTooltip>
  ) => {
    return this.api
      ?.deleteModification({
        modificationId: guidanceTooltipRule.id,
      })
      .then(this.onUpdatedTransactionRule);
  };

  public toggleGuidanceTooltipModificationVisibility = async (
    guidanceTooltipRule: Modification<GuidanceTooltipSchemaType.guidanceTooltip>,
    isVisible: boolean
  ) => {
    return this.api
      ?.updateModification({
        modificationId: guidanceTooltipRule.id,
        isActive: isVisible,
      })
      .then(this.onUpdatedTransactionRule);
  };

  public showAllGuidanceTooltipModifications = async () => {
    this.sendMessage(IActionType.showAllGuidanceTooltipModifications);
  };

  public hideAllGuidanceTooltipModifications = async () => {
    this.sendMessage(IActionType.hideAllGuidanceTooltipModifications);
  };

  public createNavigationModificationLinkEdit = async (
    rule: Modification<NavigationEditSchemaType.replaceLink>
  ) => {
    const { content, selectors } = rule;
    const modification = {
      selectors,
      schemaType: NavigationEditSchemaType.replaceLink,
      content: content as ILinkReplaceRule,
    } as Modification<NavigationEditSchemaType.replaceLink>;
    return this.api?.createModification(modification);
  };

  public setIsGuidanceTooltipOpen = (val: boolean) => {
    this.isGuidanceTooltipOpen = val;
    this.eventEmitter.emit('isGuidanceTooltipOpen', val);
  };

  public setIsGuidanceTooltipsEditLoading = (val: boolean) => {
    this.isGuidanceTooltipsEditLoading = val;
    this.eventEmitter.emit('isGuidanceTooltipsEditLoading', val);
  };
  public createNavigationModificationBlockInteractionsSettings = async ({
    isActive,
    tooltipPosition,
    tooltipText,
    tooltipTheme,
  }: Omit<IBlockInteractionsSettings, 'type'>) => {
    if (this.blockInteractionsSettings?.id) {
      return this.api?.updateModification({
        modificationId: this.blockInteractionsSettings.id,
        content: {
          isActive: isActive ?? this.blockInteractionsSettings.content.isActive,
          tooltipPosition:
            tooltipPosition ??
            this.blockInteractionsSettings.content.tooltipPosition,
          tooltipText:
            tooltipText ?? this.blockInteractionsSettings.content.tooltipText,
          tooltipTheme:
            tooltipTheme ?? this.blockInteractionsSettings.content.tooltipTheme,
        } as IBlockInteractionsSettings,
      });
    }
    return this.api?.createModification({
      schemaType: NavigationEditSchemaType.blockInteractionsSettings,
      content: {
        isActive,
        tooltipPosition,
        tooltipText,
        tooltipTheme,
      } as IBlockInteractionsSettings,
    });
  };

  public editNavigationModification = async (
    blockInteractionsRule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => {
    const { content, selectors, id } = blockInteractionsRule;
    return this.api?.updateModification({
      selectors,
      modificationId: id,
      content: content as BlockInteractionsRule,
    });
  };

  public updateFindAndReplaceLinkModification = async (
    replaceLinkModification: Modification<NavigationEditSchemaType.replaceLink>
  ) => {
    const { content, selectors, id } = replaceLinkModification;
    return this.api?.updateModification({
      selectors,
      modificationId: id,
      content: content,
    });
  };

  public deleteNavigationModification = async (
    blockInteractionsRule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => {
    return this.api?.deleteModification({
      modificationId: blockInteractionsRule.id,
    });
  };

  public reorderNavigationModification = async (
    moved: Modification<NavigationEditSchemaType.blockInteractions>,
    after?: Modification<NavigationEditSchemaType.blockInteractions>
  ) => {
    return await this.api?.reorderModification?.({
      id: moved.id,
      afterId: after?.id,
    });
  };

  public onImportEdits = async () => {
    await this.recorderSessionRefresh();
    this.sendMessage(IActionType.reloadEditorFrame);
    await this.showBackdropOverlay(true);
  };

  public createSpreadsheet = async ({ title, values }) => {
    return await this.api?.createSpreadsheet({
      title,
      values,
    });
  };
  public getSpreadsheetValues = async (spreadsheetUrl) => {
    return await this.api?.getSpreadsheetValues(spreadsheetUrl);
  };

  // groups
  public getAllSubGroupModifications = (groupId: string) => {
    return this.modifications.filter((mod) => mod.groupId === groupId);
  };

  public getGroupData = (groupId: string) => {
    return this.modifications.find(
      (mod) => (mod.content as unknown as { id: string }).id === groupId
    );
  };

  // ai prompt group creation or update
  public createAiPromptModification = async ({
    name,
    prompt,
    pages,
    modification,
  }: Omit<IAIPromptGroup, 'id'> & {
    modification?: Modification<GroupSchemaType.aiPromptGroup>;
  }) => {
    if (modification) {
      return this.api?.updateModification({
        modificationId: modification.id,
        content: {
          id: modification.content.id,
          name: name,
          prompt: prompt,
          pages: pages,
        } as IAIPromptGroup,
      });
    }

    const ruleId = uuid();
    return this.api?.createModification({
      schemaType: GroupSchemaType.aiPromptGroup,
      content: {
        name: name,
        id: ruleId,
        prompt: prompt,
        pages: pages,
      } as IAIPromptGroup,
    });
  };

  private setAiPromptModifications = async (
    aiPromptModifications: Modification<GroupSchemaType.aiPromptGroup>[]
  ) => {
    this.aiPromptGroup = aiPromptModifications;
    sdkLogger.info('AI Prompt modifications updated', {
      aiPromptModifications,
      aiPromptModificationsCount: aiPromptModifications.length,
      ...this.getAssetInfo(),
    });
    if (this.aiPromptGroup)
      await this.eventEmitter.emit(
        'updatedAiPromptGroup',
        aiPromptModifications
      );
  };

  public deleteAiPromptModification = async (
    aiPromptModification: Modification<GroupSchemaType.aiPromptGroup>
  ) => {
    await this.api?.deleteModification({
      modificationId: aiPromptModification.id,
    });
    await this.reloadEditor();
  };

  public togglePerformanceMeasuring = async (
    val: boolean,
    shouldReloadOnStart: boolean,
    shouldStopOnLoad: boolean
  ) => {
    // restart state
    this.performanceResults = cloneDeep(performanceResultsDefault);
    this.isPerformanceMeasuringStarted = val;
    await this.eventEmitter.emit('isPerformanceMeasuringStarted', val);

    if (val && shouldReloadOnStart) {
      await this.reloadEditor();
    }
    this.sendMessage(IActionType.togglePerformanceMeasuring, {
      val,
      shouldStopOnLoad,
      stopOnSelectorXpath: this.performanceSelectorXpath,
    });
  };

  public handlePerformanceResultsAvgCalculated = (
    result: EditorPerformanceResult
  ) => {
    this.performanceResults = result;
    this.eventEmitter.emit('performanceResults', result);
  };

  public handlePerformancePickSelector = () => {
    this.setInspectAction(IEditActionsType.inspect);
    this.sendMessage(IActionType.startPerformancePickSelectorMode);
    this.eventEmitter.emit('startPerformancePickSelectorMode');
  };

  public handlePerformanceStopOnSelectorXpathChange = (xpath: string) => {
    this.performanceSelectorXpath = xpath;
    this.eventEmitter.emit('updatedPerformanceSelectorXpath', xpath);
  };

  public handlePerformanceRestartButtonClick = () => {
    this.performanceResults = cloneDeep(performanceResultsDefault);
    this.eventEmitter.emit('performanceResults', this.performanceResults);
  };
}
